import Cookies from 'js-cookie';
import {UnityConfigCookieName} from '@/utils/fingerprints/FingerprintStore';
import {
  CURRENT_GAME_COOKIE_NAME,
  INSTALL_ID_COOKIE_NAME,
  INSTALLED_GAMES_LS_KEY,
} from '@/utils/constant';

function readInstalledGames(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(INSTALLED_GAMES_LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch {
    return [];
  }
}

function writeInstalledGames(ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      INSTALLED_GAMES_LS_KEY,
      JSON.stringify(Array.from(new Set(ids)))
    );
  } catch {
    /* localStorage unavailable (private mode / SSR) — ignore */
  }
}

/** True if we've already fired the install event for this game (per the localStorage record). */
export function isGameInstallRecorded(gameId: string): boolean {
  return readInstalledGames().includes(String(gameId));
}

/** Record that this game's install event has been fired (idempotent). */
export function recordInstalledGame(gameId: string): void {
  const ids = readInstalledGames();
  if (!ids.includes(String(gameId))) {
    ids.push(String(gameId));
    writeInstalledGames(ids);
  }
}

/**
 * Drain every per-game install-id cookie (`_sklz_iid<gid>`) into the localStorage
 * record, then delete the cookie. The GLOBAL `_sklz_iid` cookie is preserved.
 * Invariant: always record BEFORE removing, so a game is never "forgotten".
 */
export function drainInstallIdCookies(): void {
  if (typeof window === 'undefined') return;
  const all = Cookies.get() || {};
  Object.keys(all).forEach((name) => {
    if (name.startsWith(INSTALL_ID_COOKIE_NAME) && name !== INSTALL_ID_COOKIE_NAME) {
      const gid = name.slice(INSTALL_ID_COOKIE_NAME.length); // per-game cookie has NO separator
      if (gid) recordInstalledGame(gid);
      Cookies.remove(name, {path: '/'});
    }
  });
}

type PruneOptions = {aggressive?: boolean};

function extractTimestamp(prefix: string, value: string | undefined): number {
  if (!value) return 0;
  try {
    if (prefix === CURRENT_GAME_COOKIE_NAME) {
      // _sklz_cg value = btoa(JSON.stringify({gid, timestamp}))
      const parsed = JSON.parse(atob(value));
      return parseInt(parsed.timestamp, 10) || 0;
    }
    // i_ucc value = `<base64>|<timestamp>`
    return parseInt(value.split('|').pop() || '0', 10) || 0;
  } catch {
    return 0;
  }
}

/**
 * For one cookie family (`${prefix}_<gid>`), keep the current game's cookie plus
 * enough of the newest others to total 2. In aggressive mode keep only the current
 * game's cookie. Remove the rest with matching path so deletion actually takes.
 */
function pruneFamily(prefix: string, currentGid: string, aggressive: boolean): void {
  const all = Cookies.get() || {};
  const entries = Object.keys(all)
    .filter((name) => name.startsWith(prefix + '_'))
    .map((name) => ({name, ts: extractTimestamp(prefix, all[name])}))
    .sort((a, b) => a.ts - b.ts); // oldest first

  const currentName = `${prefix}_${currentGid}`;
  const keep = new Set<string>();
  if (all[currentName] !== undefined) keep.add(currentName); // always keep current game

  if (!aggressive) {
    for (let i = entries.length - 1; i >= 0 && keep.size < 2; i--) {
      keep.add(entries[i].name);
    }
  }

  entries.forEach(({name}) => {
    if (!keep.has(name)) {
      Cookies.remove(name, {path: '/'});
    }
  });
}

/**
 * Cap `i_ucc_*` and `_sklz_cg_*` to the newest 2 (or current-only when aggressive).
 * Each family is independent and failures are isolated.
 */
export function pruneGameCookies(currentGid: string, options: PruneOptions = {}): void {
  if (typeof window === 'undefined') return;
  const aggressive = options.aggressive ?? false;
  try {
    pruneFamily(UnityConfigCookieName, currentGid, aggressive); // 'i_ucc'
  } catch {
    /* ignore */
  }
  try {
    pruneFamily(CURRENT_GAME_COOKIE_NAME, currentGid, aggressive); // '_sklz_cg'
  } catch {
    /* ignore */
  }
}
